import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  resetPasswordCheckResetId,
  setupPasswordRequest,
} from "services/authRequests";
import { FormProvider, useForm } from "react-hook-form";
import {
  allowedPasswordCharacterRegex,
  haveDigitRegex,
  haveLowercaseRegex,
  haveUppercaseRegex,
  minimumCharactersRegex,
  minPasswordCharacters,
} from "utils/regex";
import { LoadingDots } from "utils/loadingAnimation";
import {
  MdOutlineCheckCircle,
  MdLockOpen,
  MdOutlineCancel,
  MdClose,
} from "react-icons/md";
import LandingPageBackground from "components/utils/LandingPageBackground";
import MainWindow from "components/utils/MainWindow";
import VisibilityButton from "components/login/VisibilityButton";
import { FaCircleCheck } from "react-icons/fa6";
import { usePlatform } from "components/utils/PlatformContext";
import { AnimatePresence, motion } from "framer-motion";

interface PasswordInfoPopUpProps {
  onClose: () => void;
}

/**
 * Auxiliar component used to display the requirements
 * for the password format
 * @returns
 */
const PasswordInfoPopUp: React.FC<PasswordInfoPopUpProps> = ({ onClose }) => {
  // Reference to the element
  const ref = useRef<HTMLDivElement>(null);

  /**
   * Logic to close the pop up when clicking outside this element
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      role="alert"
      aria-live="polite"
      className="absolute top-full mt-1 left-0 z-50 w-full px-3 py-2 bg-neutral-800 text-white rounded-lg shadow-lg"
    >
      <div className="flex justify-between">
        <div className="text-xs font-semibold font-sans mb-0.5">
          Only the following characters are permitted:
        </div>
        <button
          role="button"
          onClick={onClose}
          className="flex items-center justify-center rounded-full size-4 bg-neutral-450 hover:bg-neutral-300 ease-in-out duration-150 cursor-pointer"
        >
          <MdClose className="size-3 fill-white" />
        </button>
      </div>
      <ul className="list-disc pl-4">
        <li className="text-xs font-sans leading-relaxed break-all">
          Alphabetic characters (Uppercase and lowercase)
        </li>
        <li className="text-xs font-sans leading-relaxed break-all">
          Numeric digits (0 - 9)
        </li>
        <li className="text-xs font-sans leading-relaxed break-all">
          The following special characters:
          <br />
          <span className="font-mono tracking-widest">{`!"#$%&'()*+,-./:;<=>?@[\]^_\`{|}~`}</span>
        </li>
      </ul>
    </motion.div>
  );
};

const SetupPassword: React.FC = () => {
  // States definition
  const [email, setEmail] = useState("");
  const [pwdVisibility, setPwdVisibility] = useState<boolean>(false);
  const [pwdRepeatVisibility, setPwdRepeatVisibility] =
    useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordRepeat, setPasswordRepeat] = useState<string>("");
  const [setupLoading, setSetupLoading] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [requestSent, setRequestSent] = useState<boolean>(false);
  const [invalidCharField, setInvalidCharField] = useState<
    "password" | "repeat" | null
  >(null);

  // Get the platform values
  const { helpCenter } = usePlatform();

  // Define the validation conditions
  const minCharactersCheck = minimumCharactersRegex.test(password);
  const uppercaseCheck = haveUppercaseRegex.test(password);
  const lowercaseCheck = haveLowercaseRegex.test(password);
  const digitCheck = haveDigitRegex.test(password);

  // Navigate definition
  const navigate = useNavigate();

  // Form hooks
  const methods = useForm({ mode: "onChange" });

  // Get the resetTokenId
  const { resetTokenId } = useParams();

  // Check if the resetTokenId is valid, if not send again to resetPassword
  useEffect(() => {
    resetPasswordCheckResetId(resetTokenId || "").then((response) => {
      // If the response it's not successful, send to reset-password again
      if (!response.successful) {
        navigate("/reset-password");
      }

      // Set the email of the reset token
      setEmail(response.email);
    });
  }, []);

  const handlePasswordKeyDown = (field: "password" | "repeat") => {
    return (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Only intercept printable characters (length === 1 excludes Enter, Backspace, etc.)
      if (
        event.key.length === 1 &&
        !allowedPasswordCharacterRegex.test(event.key)
      ) {
        event.preventDefault();
        setInvalidCharField(field);
      }
    };
  };

  /**
   * Auxiliar function used to handle the case when pasting a new password
   * @param setValue
   * @param getValue
   * @param field
   * @returns
   */
  const handlePasswordPaste = (
    setValue: React.Dispatch<React.SetStateAction<string>>,
    getValue: () => string,
    field: "password" | "repeat",
  ) => {
    return (event: React.ClipboardEvent<HTMLInputElement>) => {
      // Prevent pasting first
      event.preventDefault();

      // Get the content
      const content = event.clipboardData.getData("text");
      const filtered = content
        .split("")
        .filter((char) => allowedPasswordCharacterRegex.test(char))
        .join("");

      // If something has been trimmed, then the content had some
      // irregular characters
      if (filtered.length < content.length) {
        setInvalidCharField(field);
      }

      // Logic for pasting in whatever position
      const input = event.currentTarget;
      const start = input.selectionStart ?? getValue().length;
      const end = input.selectionEnd ?? getValue().length;
      const currentValue = getValue();

      // And set the value
      setValue(
        currentValue.slice(0, start) + filtered + currentValue.slice(end),
      );
    };
  };

  // Function for submitting
  const handlePasswordSubmit = methods.handleSubmit(async () => {
    // Check avoid run this function again after being executed
    if (setupLoading) return;

    // Set animation state
    setSetupLoading(true);

    // Define the object to send
    const dataToSend = {
      resetTokenId: resetTokenId || "",
      password: password || "",
    };

    // Send the reset password information for checking
    const response: {
      successful: boolean;
      message: string;
      redirectToReset: boolean;
    } = await setupPasswordRequest(dataToSend);

    // Stop the animation
    setSetupLoading(false);

    // If the response is successful
    if (response.successful) {
      // Then go to the home page
      setShowError(false);
      // Show the successful view
      setRequestSent(true);
    } else {
      // Display an error message
      setShowError(true);
    }

    // Check if there is necessary to redirect
    if (response.redirectToReset) navigate("/reset-password");
  });

  return (
    <LandingPageBackground>
      <MainWindow>
        <div
          className={`self-stretch h-full flex-col ${
            requestSent ? "justify-center" : "justify-start"
          } items-center gap-8 inline-flex`}
        >
          {!requestSent && (
            <>
              <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
                <div className="text-neutral-700 text-[33px] font-semibold font-sans leading-[34px]">
                  Set new password
                </div>
                <div className="self-stretch h-fit flex-col justify-start items-start gap-1 flex">
                  <div className="grow shrink basis-0 text-neutral-700 text-base font-normal font-sans leading-[21px]">
                    Please set your password for
                  </div>
                  <div className="grow shrink basis-0 text-neutral-700 text-base font-semibold font-sans leading-[21px]">
                    {email}
                  </div>
                </div>
              </div>
              <div className="self-stretch h-[183px] flex-col justify-center items-start gap-4 flex">
                <div className="grow shrink basis-0 text-neutral-700 text-base font-normal font-sans leading-[19px]">
                  Your password must have at least:
                </div>
                <div className="self-stretch h-[148px] flex-col justify-start items-start gap-3 flex">
                  <div className="self-stretch justify-start items-center gap-2 inline-flex">
                    {minCharactersCheck ? (
                      <MdOutlineCheckCircle className="w-5 h-5 fill-accent-500" />
                    ) : (
                      <MdOutlineCancel className="w-5 h-5 fill-neutral-700" />
                    )}
                    <div className="h-[19px] text-neutral-700 text-base font-normal font-sans leading-[19px]">
                      {minPasswordCharacters} Characters
                    </div>
                  </div>
                  <div className="self-stretch justify-start items-center gap-2 inline-flex">
                    {uppercaseCheck ? (
                      <MdOutlineCheckCircle className="w-5 h-5 fill-accent-500" />
                    ) : (
                      <MdOutlineCancel className="w-5 h-5 fill-neutral-700" />
                    )}
                    <div className="h-[19px] text-neutral-700 text-base font-normal font-sans leading-[19px]">
                      1 uppercase character
                    </div>
                  </div>
                  <div className="self-stretch justify-start items-center gap-2 inline-flex">
                    {lowercaseCheck ? (
                      <MdOutlineCheckCircle className="w-5 h-5 fill-accent-500" />
                    ) : (
                      <MdOutlineCancel className="w-5 h-5 fill-neutral-700" />
                    )}
                    <div className="h-[19px] text-neutral-700 text-base font-normal font-sans leading-[19px]">
                      1 lowercase character
                    </div>
                  </div>
                  <div className="self-stretch justify-start items-center gap-2 inline-flex">
                    {digitCheck ? (
                      <MdOutlineCheckCircle className="w-5 h-5 fill-accent-500" />
                    ) : (
                      <MdOutlineCancel className="w-5 h-5 fill-neutral-700" />
                    )}
                    <div className="h-[19px] text-neutral-700 text-base font-normal font-sans leading-[19px]">
                      1 digit
                    </div>
                  </div>
                  <div className="self-stretch justify-start items-center gap-2 inline-flex">
                    {password &&
                    passwordRepeat &&
                    password === passwordRepeat ? (
                      <MdOutlineCheckCircle className="w-5 h-5 fill-accent-500" />
                    ) : (
                      <MdOutlineCancel className="w-5 h-5 fill-neutral-700" />
                    )}
                    <div className="h-[19px] text-neutral-700 text-base font-normal font-sans leading-[19px]">
                      Passwords must match
                    </div>
                  </div>
                </div>
              </div>
              <FormProvider {...methods}>
                <form
                  id="setup-password-form"
                  className="w-full"
                  onSubmit={(event) => event.preventDefault()}
                  noValidate
                  autoComplete="off"
                >
                  <div className="self-stretch h-[236px] flex-col justify-start items-start gap-6 flex">
                    <div className="self-stretch h-[71px] flex-col justify-start items-start gap-1.5 flex">
                      <div className="rounded justify-start items-start gap-2 inline-flex">
                        <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none">
                          Password
                        </div>
                      </div>
                      <div className="w-full h-full rounded-lg bg-neutral-50 text-neutral-700 placeholder:text-neutral-300 text-base font-normal font-sans leading-[19px] flex items-center relative">
                        <MdLockOpen
                          className={`absolute left-3.5 w-5 h-5 ${setupLoading ? "fill-neutral-300" : "fill-neutral-500"}`}
                        />
                        <input
                          id="password"
                          disabled={setupLoading}
                          type={pwdVisibility ? "text" : "password"}
                          className={`w-full h-full pl-[42px] pr-12 rounded-lg autofill:shadow-[inset_0_0_0px_1000px_rgb(250,250,250)] ${
                            setupLoading
                              ? "text-neutral-300 bg-neutral-100 autofill:[-webkit-text-fill-color:theme(colors.neutral.300)]"
                              : "text-neutral-700 bg-transparent autofill:[-webkit-text-fill-color:theme(colors.neutral.700)]"
                          } placeholder:text-neutral-300 placeholder:select-none focus:outline-none text-base font-normal font-sans leading-[19px]`}
                          placeholder="Enter your password"
                          autoComplete="new-password"
                          value={password}
                          onChange={(event) => setPassword(event.target.value)}
                          onKeyDown={handlePasswordKeyDown("password")}
                          onPaste={handlePasswordPaste(
                            setPassword,
                            () => password,
                            "password",
                          )}
                        />
                        <VisibilityButton
                          visibility={pwdVisibility}
                          setVisibility={setPwdVisibility}
                          active={!setupLoading}
                        />
                        <AnimatePresence>
                          {invalidCharField === "password" && (
                            <PasswordInfoPopUp
                              onClose={() => setInvalidCharField(null)}
                            />
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                    <div className="self-stretch h-[71px] flex-col justify-start items-start gap-1.5 flex">
                      <div className="rounded justify-start items-start gap-2 inline-flex">
                        <div className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide select-none">
                          Confirm Password
                        </div>
                      </div>
                      <div className="w-full h-full rounded-lg bg-neutral-50 text-neutral-700 placeholder:text-neutral-300 text-base font-normal font-sans leading-[19px] flex items-center relative">
                        <MdLockOpen
                          className={`absolute left-3.5 w-5 h-5 ${setupLoading ? "fill-neutral-300" : "fill-neutral-500"}`}
                        />
                        <input
                          id="repeat-password"
                          disabled={setupLoading}
                          type={pwdRepeatVisibility ? "text" : "password"}
                          className={`w-full h-full pl-[42px] pr-12 rounded-lg autofill:shadow-[inset_0_0_0px_1000px_rgb(250,250,250)] ${
                            setupLoading
                              ? "text-neutral-300 bg-neutral-100 autofill:[-webkit-text-fill-color:theme(colors.neutral.300)]"
                              : "text-neutral-700 bg-transparent autofill:[-webkit-text-fill-color:theme(colors.neutral.700)]"
                          } placeholder:text-neutral-300 placeholder:select-none focus:outline-none text-base font-normal font-sans leading-[19px]`}
                          placeholder="Confirm the password"
                          autoComplete="new-password"
                          value={passwordRepeat}
                          onChange={(event) =>
                            setPasswordRepeat(event.target.value)
                          }
                          onKeyDown={handlePasswordKeyDown("repeat")}
                          onPaste={(event) => event.preventDefault()} // Not allow pasting in this field
                        />
                        <VisibilityButton
                          visibility={pwdRepeatVisibility}
                          setVisibility={setPwdRepeatVisibility}
                          active={!setupLoading}
                        />
                        <AnimatePresence>
                          {invalidCharField === "repeat" && (
                            <PasswordInfoPopUp
                              onClose={() => setInvalidCharField(null)}
                            />
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                    {lowercaseCheck &&
                    uppercaseCheck &&
                    digitCheck &&
                    minCharactersCheck &&
                    password === passwordRepeat ? (
                      <button
                        id="confirm-new-password"
                        className={`self-stretch px-5 py-3 ${
                          setupLoading
                            ? "bg-disabled cursor-not-allowed"
                            : "bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 cursor-pointer"
                        } rounded justify-center items-center gap-2 inline-flex`}
                        onClick={handlePasswordSubmit}
                        disabled={setupLoading}
                      >
                        <div className="text-white text-lg font-semibold font-sans leading-snug select-none">
                          {setupLoading ? (
                            <LoadingDots />
                          ) : (
                            "Confirm new password"
                          )}
                        </div>
                      </button>
                    ) : (
                      <button
                        className="self-stretch px-5 py-3 bg-Subtle rounded justify-center items-center gap-2 inline-flex"
                        disabled
                      >
                        <div className="text-neutral-400 text-lg font-semibold font-sans leading-snug select-none">
                          Confirm new password
                        </div>
                      </button>
                    )}
                  </div>
                </form>
              </FormProvider>
            </>
          )}
          {requestSent && (
            <>
              <div className="self-stretch h-fit flex-col justify-start items-start gap-6 flex">
                <div className="self-stretch justify-start items-center gap-2 inline-flex">
                  <div className="w-10 h-10 relative bg-white rounded-full">
                    <FaCircleCheck className="w-full h-full fill-accent-600" />
                  </div>
                  <div className="grow shrink basis-0 text-neutral-700 text-[33px] font-semibold font-sans leading-[34px]">
                    You’re all set!
                  </div>
                </div>
                <div className="self-stretch h-fill flex-col justify-start items-start gap-3 flex">
                  <div className="grow shrink basis-0 text-neutral-700 text-base font-normal font-sans leading-[21px]">
                    Your Password has been reset successfully!
                  </div>
                  <div className="grow shrink basis-0 text-neutral-700 text-base font-normal font-sans leading-[21px]">
                    Sign In to start learning
                  </div>
                </div>
              </div>
              <Link
                to="/"
                className="self-stretch px-5 py-3 bg-secondary-600 hover:bg-secondary-700 ease-in-out duration-150 rounded justify-center items-center gap-2 inline-flex"
              >
                <div className="text-white text-lg font-semibold font-sans leading-snug">
                  Sign In to your account
                </div>
              </Link>
            </>
          )}
          <div className="w-full justify-center items-center gap-2 inline-flex">
            <Link
              to={`mailto:${helpCenter}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="text-accent-700 hover:text-accent-800 text-sm font-semibold font-sans underline leading-[17px] select-none">
                Email support
              </div>
            </Link>
          </div>
        </div>
      </MainWindow>
    </LandingPageBackground>
  );
};

export default SetupPassword;
